import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createGlobalStyle, keyframes } from "styled-components";
import styled from "styled-components";
import SiteFooter from "../components/SiteFooter";
import { useLang } from "../i18n/LanguageContext";
import { requestPay } from "../utils/pay";

declare global { interface Window { IMP: any } }

const Global = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;700;900&family=Pretendard:wght@400;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    background: #FBF6EA;
    font-family: 'Pretendard', 'Malgun Gothic', sans-serif;
    min-height: 100vh;
  }
`;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); }`;
const floatUp = keyframes`0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); }`;
const shimmer = keyframes`0% { opacity: 0.7; } 50% { opacity: 1; } 100% { opacity: 0.7; }`;

/* ── 면책 모달 ─────────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(44, 24, 16, 0.72);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.3s ease;
`;
const ModalBox = styled.div`
  background: #FBF6EA;
  border-radius: 24px;
  max-width: 400px; width: 100%;
  padding: 36px 28px 28px;
  text-align: center;
  position: relative;
`;
const ModalIcon = styled.div`font-size: 2.4rem; margin-bottom: 12px; animation: ${shimmer} 2s infinite;`;
const ModalTitle = styled.div`
  font-size: 1.15rem; font-weight: 900;
  color: #2B2A26; margin-bottom: 16px; line-height: 1.5;
`;
const ModalText = styled.div`
  font-size: 0.82rem; color: #5A3E2E; line-height: 1.9;
  margin-bottom: 24px;
  background: #FFF4E0;
  border-radius: 12px; padding: 14px 16px;
  text-align: left;
`;
const ModalBtn = styled.button`
  width: 100%;
  background: #B8862A;
  color: #fff; border: 2px solid #2B2A26; border-radius: 50px;
  padding: 14px; font-size: 0.95rem; font-weight: 800;
  cursor: pointer; font-family: inherit;
  transition: transform 0.15s;
  &:active { transform: scale(0.97); }
`;

/* ── 전체 래퍼 ─────────────────────────────── */
const Wrap = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 0 0 40px;
  animation: ${fadeIn} 0.5s ease;
`;

/* ── 상단 단청 장식 바 ─────────────────────── */
const TopBar = styled.div`
  height: 8px;
  background: repeating-linear-gradient(
    90deg,
    #D0654F 0px, #D0654F 20px,
    #B8862A 20px, #B8862A 40px,
    #5E8A4E 40px, #5E8A4E 60px,
    #4A7BA8 60px, #4A7BA8 80px,
    #B8862A 80px, #B8862A 100px
  );
`;

/* ── 히어로 섹션 ────────────────────────────── */
const Hero = styled.div`
  background: #FBF6EA;
  padding: 36px 20px 28px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;
const CharacterWrap = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
`;
const CharacterImg = styled.img`
  width: 150px; height: 150px;
  object-fit: contain;
  animation: ${floatUp} 3.5s ease-in-out infinite;
`;
const CharacterBadge = styled.div`
  position: absolute; bottom: 6px; right: -8px;
  background: #D0654F; color: #fff;
  font-size: 0.62rem; font-weight: 800;
  padding: 3px 9px; border-radius: 20px;
  white-space: nowrap;
`;
const BrandName = styled.h1`
  font-size: 2rem; font-weight: 900;
  color: #2B2A26; letter-spacing: -0.5px;
  margin-bottom: 6px;
`;
const BrandSub = styled.div`
  font-size: 0.8rem; color: #7A5C42;
  letter-spacing: 0.8px;
  margin-bottom: 14px;
`;
const RatingRow = styled.div`
  display: flex; justify-content: center; gap: 10px;
  flex-wrap: wrap;
`;
const RatingBadge = styled.div`
  display: flex; align-items: center; gap: 5px;
  background: #fff;
  border: 1.5px solid #2B2A26;
  border-radius: 20px; padding: 5px 13px;
  font-size: 0.72rem; color: #5A3E2E; font-weight: 700;
`;

/* ── 섹션 제목 ──────────────────────────────── */
const SectionTitle = styled.div`
  font-size: 1rem; font-weight: 900;
  color: #2B2A26;
  padding: 24px 20px 12px;
  display: flex; align-items: center; gap: 10px; justify-content: space-between;
`;

/* ── 서비스 카드 — 씬 이미지 배경 + 누끼 캐릭터 ─── */
const CardWrap = styled.div`
  padding: 0 16px;
  display: flex; flex-direction: column; gap: 16px;
  /* 폰은 가로가 좁다. 좌우 여백을 줄여 글이 들어갈 자리를 넓힌다. */
  @media (max-width: 400px) { padding: 0 8px; gap: 13px; }
`;
const ServiceCard = styled.div`
  border-radius: 22px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s;
  background: #fff;
  border: 2px solid #2B2A26;
  position: relative;
  &:active { transform: scale(0.99); }
  &:hover { transform: translateY(-3px); }
`;
/* 쿠키사주가 공짜라는 걸 한눈에.
   ★ 카드 위에 띄우면 아래 글자를 덮는다(2026-08-08에 그렇게 만들었다가 고침).
     라벨 줄 자리를 뱃지가 대신하게 해서, 덮을 것도 없고 같은 말이 두 번
     나오지도 않게 한다. 금액은 적지 않는다(쿠키사주 금액 노출 금지 규칙). */
const TitleRow = styled.div`
  display: flex; align-items: center; gap: 9px; flex-wrap: nowrap;
`;
const FreePill = styled.span`
  display: inline-block;
  /* 흰 글자를 쓸 거면 배경은 검정이어야 대비가 산다.
     파스텔 배경 위에 산호색+흰글자+검은그림자를 겹치면 탁해진다.
     자리는 제목 오른쪽 — 가장 큰 글자 옆이라 시선이 먼저 닿는다. */
  background: #2B2A26;
  color: #FFF8F0;
  border: none;
  border-radius: 999px;
  padding: 5px 15px 6px;
  font-size: 0.86rem;
  font-weight: 900;
  letter-spacing: 0.6px;
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
  @media (max-width: 400px) { padding: 4px 12px 5px; font-size: 0.8rem; }
`;
/* 파스텔 헤더 — 왼쪽 제목 + 오른쪽 누끼 캐릭터 */
const CardBg = styled.div<{ $color: string }>`
  width: 100%; min-height: 128px;
  background: ${p => p.$color};
  position: relative;
  display: flex; align-items: center;
  padding: 18px 20px;
  @media (max-width: 400px) { padding: 16px 13px; min-height: 118px; }
`;
const CardHeadText = styled.div`
  text-align: left; z-index: 2; max-width: 62%;
  @media (max-width: 400px) { max-width: 68%; }
`;
/* 누끼 캐릭터 — 파스텔 헤더 오른쪽에 떠있음 */
const CardCharacter = styled.img`
  position: absolute; bottom: 4px; right: 14px; z-index: 1;
  width: 104px; height: auto;
  @media (max-width: 400px) { width: 88px; right: 8px; }
  object-fit: contain;
  animation: ${floatUp} 3s ease-in-out infinite;
`;
/* 카드 하단 콘텐츠 */
const CardContent = styled.div<{ $color: string }>`
  padding: 14px 18px 18px;
  @media (max-width: 400px) { padding: 12px 13px 14px; }
`;
const CardLabel = styled.div<{ $color: string }>`
  font-size: 0.64rem; font-weight: 800; color: ${p => p.$color};
  letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 4px;
`;
const CardTitle = styled.div`
  font-size: 1.5rem; font-weight: 900; color: #2B2A26; letter-spacing:-0.02em; line-height:1.15;
`;
/* 제목 바로 아래 설명 — 파스텔 헤더 안 */
const CardHeadDesc = styled.div`
  font-size: 0.76rem; color: #2B2A26; font-weight: 500; line-height: 1.5; margin-top: 6px;
`;
const CardPrice = styled.div<{ $color: string }>`
  font-size: 0.72rem; color: ${p => p.$color}; font-weight: 700; margin-bottom: 8px;
`;
const CardDesc = styled.div`
  font-size: 0.82rem; color: #6B6355; line-height: 1.65; margin-bottom: 14px;
`;
const CardBtn = styled.div<{ $bg: string }>`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${p => p.$bg}; color: #fff; font-weight: 800;
  font-size: 0.85rem; padding: 11px 22px; border-radius: 12px;
  border: 2px solid #2B2A26;
`;

/* ── 아코디언 카드 ───────────────────────────── */
const AccCard = styled.div`
  margin: 0 16px 12px;
  background: #fff;
  border: 1.5px solid #2B2A26;
  border-radius: 20px;
  overflow: hidden;
`;
const AccHeader = styled.button`
  width: 100%; background: none; border: none;
  padding: 20px; cursor: pointer;
  display: flex; align-items: center; gap: 12px;
  font-family: inherit;
`;
const AccArrow = styled.span<{ $open: boolean }>`
  color: #B8862A; font-size: 1rem;
  transition: transform 0.2s;
  transform: ${p => p.$open ? "rotate(90deg)" : "none"};
  margin-left: auto;
`;
const AccBody = styled.div`
  padding: 0 20px 20px;
`;
const ReviewBadge = styled.span`
  background:#FBE0D9; border:1.5px solid #2B2A26; border-radius:999px;
  padding:3px 11px; font-size:.68rem; font-weight:800; color:#2B2A26; white-space:nowrap;
`;

/* ── 상품 ─────────────────────────────────────── */
// 상담 종류는 온라인·방문·전화가 모두 같아야 한다(8종).
// 결혼운·재회운은 따로 팔지 않는다 — 재회운은 연애운 안에,
// 결혼운은 심층궁합 안에 내용이 들어가 있다.
const PHONE_SERVICES = [
  { key: "종합운세",  price: 50000 },
  { key: "신년운세",  price: 30000 },
  { key: "연애운",    price: 30000 },
  { key: "직업운",    price: 30000 },
  { key: "사업운",    price: 30000 },
  { key: "건강운",    price: 30000 },
  { key: "연애궁합",  price: 50000 },
  { key: "심층궁합",  price: 80000 },
];

const NAVER_URL = "https://naver.me/xZjIiBpV";
const KAKAO_URL = "https://place.map.kakao.com/2033969175";
const PHONE_NUM = "010-4282-2377";
const IMP_CODE  = process.env.REACT_APP_IMP_CODE || "imp00000000";
const API_BASE  = process.env.REACT_APP_API_URL || "";

export default function MainPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [openSection, setOpenSection] = useState<"phone"|"visit"|null>(null);
  const [selectedSvc, setSelectedSvc] = useState(PHONE_SERVICES[0].key);
  const [payResult, setPayResult] = useState("");

  useEffect(() => {
    if (!sessionStorage.getItem("woo_disclaimer_ok")) {
      setShowDisclaimer(true);
    }
  }, []);

  /* 휴대폰으로 전화상담을 결제하고 돌아왔을 때.
     휴대폰 결제는 콜백 없이 주소 이동으로 돌아오기 때문에, 이 처리가 없으면
     결제는 됐는데 관리자 대기목록에 아무 기록도 안 남는다 — 누가 냈는지 알 수가 없다. */
  useEffect(() => {
    // 결제 회사가 이미 물음표가 있는 주소에 또 물음표로 붙이는 경우가 있어 정리해서 읽는다
    const raw = (window.location.search || "").replace(/\?/g, (_m, i) => (i === 0 ? "?" : "&"));
    const q = new URLSearchParams(raw);
    const impUid = q.get("imp_uid") || "";
    const success = q.get("imp_success") ?? q.get("success");
    if (!impUid && success === null) return;

    let saved: any = null;
    try { saved = JSON.parse(sessionStorage.getItem("phonePayPending") || "null"); } catch { /* noop */ }
    try { sessionStorage.removeItem("phonePayPending"); } catch { /* noop */ }
    window.history.replaceState({}, "", window.location.pathname);
    if (success === "false" || success === "0") {
      setPayResult(t("main.payFail", { msg: q.get("error_msg") || "" }));
      return;
    }
    if (!saved?.service) return;

    setOpenSection("phone");
    setSelectedSvc(saved.service);
    setPayResult(t("main.paySuccess", { phone: PHONE_NUM }));
    fetch(`${API_BASE}/api/v1/quick/phone-pay`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service: saved.service, amount: saved.amount,
        imp_uid: impUid, merchant_uid: q.get("merchant_uid") || saved.merchantUid,
      }),
    }).catch(() => { /* 기록 실패해도 결제는 완료 — 화면 안내는 이미 나갔다 */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 결과지 하단 '전화·방문 상담 보기'로 들어온 경우 — 해당 자리를 열어 놓고 내려준다.
     그냥 메인으로 보내면 접힌 아코디언을 다시 찾아야 해서 대부분 거기서 끝난다. */
  useEffect(() => {
    const sec = new URLSearchParams(window.location.search).get("open");
    if (sec !== "phone" && sec !== "visit") return;
    setOpenSection(sec);
    window.history.replaceState({}, "", "/");
    // 아코디언이 펼쳐진 뒤에 그 자리로 내려간다
    setTimeout(() => {
      document.getElementById(`section-${sec}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  }, []);

  const closeDisclaimer = () => {
    sessionStorage.setItem("woo_disclaimer_ok", "1");
    setShowDisclaimer(false);
  };

  const toggle = (s: "phone"|"visit") => setOpenSection(p => p === s ? null : s);

  const handlePhonePayment = () => {
    const svc = PHONE_SERVICES.find(s => s.key === selectedSvc)!;
    const merchantUid = `phone_${selectedSvc}_${Date.now()}`;
    if (typeof window.IMP === "undefined") {
      setPayResult(t("main.payLoading"));
      return;
    }
    // 휴대폰 결제는 주소 이동으로 돌아와 화면 상태가 날아간다 — 먼저 적어둔다
    try {
      sessionStorage.setItem("phonePayPending", JSON.stringify({
        service: svc.key, amount: svc.price, merchantUid,
      }));
    } catch { /* 저장 실패해도 PC 결제는 정상 동작 */ }
    requestPay({
      merchantUid,
      name: t("main.payName", { svc: t(`svc.${svc.key}`) }),
      amount: svc.price,
      // 돌아올 주소에 물음표를 달지 않는다(결제번호가 또 붙으면 주소가 깨진다)
      redirectUrl: `${window.location.origin}/`,
    }).then((rsp) => {
      if (rsp.success) {
        setPayResult(t("main.paySuccess", { phone: PHONE_NUM }));
        try { sessionStorage.removeItem("phonePayPending"); } catch { /* noop */ }
        // 결제 성공 → 서버에 기록(관리자 대기목록에 '전화상담'으로 표시)
        try {
          fetch(`${API_BASE}/api/v1/quick/phone-pay`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              service: svc.key,
              amount: svc.price,
              imp_uid: rsp.imp_uid,
              merchant_uid: rsp.merchant_uid,
            }),
          }).catch(() => { /* 기록 실패해도 결제는 완료 */ });
        } catch { /* noop */ }
      }
      else setPayResult(t("main.payFail", { msg: rsp.error_msg || "" }));
    });
  };

  return (
    <>
      <Global />

      {/* 면책 모달 */}
      {showDisclaimer && (
        <ModalOverlay onClick={closeDisclaimer}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalIcon>🐉</ModalIcon>
            <ModalTitle>{t("main.modalTitleA")}<br />{t("main.modalTitleB")}</ModalTitle>
            <ModalText>
              {t("main.modalText1")}<br /><br />
              {t("main.modalText2")}<br /><br />
              {t("main.modalText3")}
            </ModalText>
            <ModalBtn onClick={closeDisclaimer}>{t("main.modalBtn")}</ModalBtn>
          </ModalBox>
        </ModalOverlay>
      )}


      <Wrap>
        {/* 히어로 */}
        <Hero>
          <CharacterWrap>
            <CharacterImg src="/woo_y_1.webp" alt="우연 사주 캐릭터" />
          </CharacterWrap>
          <BrandName>{t("main.brandName")}</BrandName>
          <BrandSub>{t("main.brandSub")}</BrandSub>
          <RatingRow>
            <RatingBadge>{t("main.ratingNaver")}</RatingBadge>
            <RatingBadge>{t("main.ratingKakao")}</RatingBadge>
            <RatingBadge>{t("main.ratingChannels")}</RatingBadge>
          </RatingRow>
        </Hero>

        {/* 서비스 카드 */}
        <SectionTitle>{t("main.sectionServices")}<ReviewBadge>{t("main.heroBadge")}</ReviewBadge></SectionTitle>
        <CardWrap>

          {/* 쿠키사주 — woo_06 씬 배경 + woo_y_6 누끼 */}
          <ServiceCard onClick={() => navigate("/quick")}>
            <CardBg $color="#FBE0D9">
              <CardHeadText>
                <CardLabel $color="#A84534">{t("main.cookieLabel")}</CardLabel>
                <TitleRow>
                  <CardTitle>{t("main.cookieTitle")}</CardTitle>
                  <FreePill>{t("main.cookieBadge")}</FreePill>
                </TitleRow>
                <CardHeadDesc>
                  {t("main.cookieDesc1")}<br />
                  {t("main.cookieDesc2")}
                </CardHeadDesc>
              </CardHeadText>
              <CardCharacter src="/woo_y_6.webp" alt="우연" />
            </CardBg>
            <CardContent $color="#E8604C">
              {/* 쿠키(무료) 결과는 메일로 보내지 않는다 — 메일은 결제하신 분에게만 */}
              <CardDesc>
                <span style={{ color: "#2B2A26", fontWeight: 300 }}>{t("main.cookieFreeNote")}</span>
              </CardDesc>
              <CardBtn $bg="#E8604C">{t("main.cookieBtn")}</CardBtn>
            </CardContent>
          </ServiceCard>

          {/* 온라인 사주 — woo_01 씬 배경 + woo_y_4 두루마리 누끼 */}
          <ServiceCard onClick={() => navigate("/client")}>
            <CardBg $color="#E7F0E2">
              <CardHeadText>
                <CardLabel $color="#5E8A4E">{t("main.onlineLabel")}</CardLabel>
                <CardTitle>{t("main.onlineTitle")}</CardTitle>
                <CardHeadDesc>
                  {t("main.onlineDesc1")}<br />
                  {t("main.onlineDesc2")}
                </CardHeadDesc>
              </CardHeadText>
              <CardCharacter src="/woo_y_4.webp" alt="두루마리" style={{ width: 80 }} />
            </CardBg>
            <CardContent $color="#5E8A4E">
              <CardDesc>
                <span style={{ color: "#2B2A26", fontWeight: 300 }}>{t("main.emailDelivery")}</span>
              </CardDesc>
              <CardBtn $bg="#5E8A4E">{t("main.onlineBtn")}</CardBtn>
            </CardContent>
          </ServiceCard>

          {/* 우연의 화살 (큐피드 유료) — woo_y_1 누끼 */}
          <ServiceCard onClick={() => navigate("/cupid")}>
            <CardBg $color="#F8D9E1">
              <CardHeadText>
                <CardLabel $color="#B85C7A">{t("main.cupidLabel")}</CardLabel>
                <CardTitle>{t("main.cupidTitle")}</CardTitle>
                <CardHeadDesc>
                  {t("main.cupidDesc1")}<br />
                  {t("main.cupidDesc2")}
                </CardHeadDesc>
              </CardHeadText>
              <CardCharacter src="/woo_y_1.webp" alt="우연의 화살" />
            </CardBg>
            <CardContent $color="#B85C7A">
              <CardBtn $bg="#B85C7A">{t("main.cupidBtn")}</CardBtn>
            </CardContent>
          </ServiceCard>

          {/* 방문 상담 — woo_02 씬 배경 + woo_y_2 누끼 */}
          <ServiceCard onClick={() => navigate("/visit")}>
            <CardBg $color="#FBEFCF">
              <CardHeadText>
                <CardLabel $color="#B8862A">{t("main.visitLabel")}</CardLabel>
                <CardTitle>{t("main.visitTitle")}</CardTitle>
                <CardHeadDesc>
                  {t("main.visitDesc1")}
                </CardHeadDesc>
              </CardHeadText>
              <CardCharacter src="/woo_y_2.webp" alt="우연" />
            </CardBg>
            <CardContent $color="#B8862A">
              <div style={{ fontSize: "0.74rem", color: "#C0392B", fontWeight: 700, lineHeight: 1.45, marginBottom: 12 }}>
                {t("main.visitOnlyNote")}
              </div>
              <CardBtn $bg="#B8862A">{t("main.visitBtn")}</CardBtn>
            </CardContent>
          </ServiceCard>
        </CardWrap>

        {/* 전화상담 */}
        <SectionTitle id="section-phone" style={{ marginTop: 8 }}>{t("main.sectionPhone")}</SectionTitle>
        <AccCard>
          <AccHeader onClick={() => toggle("phone")}>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontWeight: 900, color: "#2B2A26", fontSize: "0.98rem" }}>{t("main.phoneHeaderTitle")}</div>
              <div style={{ fontSize: "0.7rem", color: "#8C8677", marginTop: 2 }}>{t("main.phoneHeaderSub")}</div>
            </div>
            <AccArrow $open={openSection === "phone"}>▶</AccArrow>
          </AccHeader>
          {openSection === "phone" && (
            <AccBody>
              <a href={`tel:${PHONE_NUM.replace(/-/g,"")}`} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "#5E8A4E", border: "2px solid #2B2A26",
                color: "#fff", textDecoration: "none", borderRadius: 14,
                padding: "13px", fontWeight: 800, fontSize: "0.95rem",
                marginBottom: 18, boxShadow: "none",
              }}>
                {t("main.phoneCallBtn", { phone: PHONE_NUM })}
              </a>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#8C8677", marginBottom: 10 }}>
                {t("main.phonePayLabel")}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                {PHONE_SERVICES.map(s => (
                  <button key={s.key} onClick={() => setSelectedSvc(s.key)} style={{
                    padding: "10px 8px", borderRadius: 10, cursor: "pointer",
                    border: selectedSvc === s.key ? "1.5px solid #2B2A26" : "1px solid #C9C0AC",
                    background: selectedSvc === s.key ? "#FFF8E8" : "#fff",
                    fontFamily: "inherit", textAlign: "left",
                  }}>
                    <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#2B2A26" }}>{t(`svc.${s.key}`)}</div>
                    <div style={{ fontSize: "0.75rem", color: "#B8862A", marginTop: 2 }}>
                      {t("main.priceLabel", { price: s.price.toLocaleString() })}
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={handlePhonePayment} style={{
                width: "100%", padding: "14px",
                background: "#B8862A",
                color: "#fff", border: "2px solid #2B2A26", borderRadius: 14,
                fontSize: "0.95rem", fontWeight: 800, cursor: "pointer",
                boxShadow: "none", fontFamily: "inherit",
              }}>
                {t("main.payBtn", { price: PHONE_SERVICES.find(s=>s.key===selectedSvc)?.price.toLocaleString() || "" })}
              </button>
              {payResult && (
                <div style={{
                  marginTop: 12, padding: "10px 14px",
                  background: payResult.startsWith("✅") ? "#ebfbee" : "#FBE0D9",
                  borderRadius: 10, fontSize: "0.82rem", color: "#2B2A26",
                  border: `1px solid ${payResult.startsWith("✅") ? "#8ce99a" : "#ffc9c9"}`,
                }}>{payResult}</div>
              )}
            </AccBody>
          )}
        </AccCard>

        {/* 방문 예약 */}
        <AccCard id="section-visit">
          <AccHeader onClick={() => toggle("visit")}>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontWeight: 900, color: "#2B2A26", fontSize: "0.98rem" }}>{t("main.visitHeaderTitle")}</div>
              <div style={{ fontSize: "0.7rem", color: "#8C8677", marginTop: 2 }}>{t("main.visitHeaderSub")}</div>
            </div>
            <AccArrow $open={openSection === "visit"}>▶</AccArrow>
          </AccHeader>
          {openSection === "visit" && (
            <AccBody>
              <div style={{ fontSize: "0.78rem", color: "#8C8677", margin: "14px 0", lineHeight: 1.7 }}>
                {t("main.visitInfo1")}<br />
                {t("main.visitInfo2")}
              </div>
              <a href={NAVER_URL} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "#456B3A", border: "2px solid #2B2A26", color: "#fff", textDecoration: "none",
                borderRadius: 14, padding: "13px", fontWeight: 800, fontSize: "0.95rem",
                marginBottom: 10, boxShadow: "none",
              }}>
                {t("main.naverBook")}
              </a>
              <a href={KAKAO_URL} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "#C9A227", border: "2px solid #2B2A26", color: "#2B2A26", textDecoration: "none",
                borderRadius: 14, padding: "13px", fontWeight: 800, fontSize: "0.95rem",
                boxShadow: "none",
              }}>
                {t("main.kakaoBook")}
              </a>
            </AccBody>
          )}
        </AccCard>

        <div style={{ padding: "0 16px" }}>
          <SiteFooter />
        </div>
      </Wrap>
    </>
  );
}
